export const EMAIL = "support@sparklinkitsolutions.com";

export const marqueeItems = [
  "Ideas into software.",
  "Complexity into clarity.",
  "Manual work into automation.",
  "Legacy into modern.",
  "Data into insight.",
  "Technology into opportunity.",
  "Built for today. Ready for tomorrow.",
  "Software that works for your business.",
  "Engineering beyond the code.",
  "Think. Build. Evolve.",
];

export const services = [
  {
    title: "Custom Software Development",
    slug: "custom-software-development",
    short: "Purpose-built applications designed around your unique business requirements, workflows, and users.",
    long: "From internal business systems to complex enterprise applications, we create software designed to fit your organization — not the other way around.",
    points: ["Internal business systems", "Enterprise applications", "Workflow-driven platforms", "Built around your users"],
  },
  {
    title: "Web & Enterprise Applications",
    slug: "web-enterprise-applications",
    short: "Modern, scalable web applications built for performance, reliability, and long-term growth.",
    long: "We develop business platforms, dashboards, management systems, portals, and enterprise applications using modern development practices.",
    points: ["Business platforms", "Dashboards", "Management systems", "Portals"],
  },
  {
    title: "Mobile App Development",
    slug: "mobile-app-development",
    short: "Powerful mobile experiences designed around your users, business, and long-term goals.",
    long: "We build modern mobile applications that combine intuitive experiences with reliable technology — helping businesses connect with customers, streamline operations, and extend their digital capabilities.",
    points: ["iOS & Android applications","Cross-platform development","Customer & business apps","API & backend integration","Performance & scalability"],
  },
  {
    title: "API & Backend Engineering",
    slug: "api-backend-engineering",
    short: "Reliable backend systems that connect applications, services, data, and business processes.",
    long: "We design scalable APIs and backend architectures that provide a strong foundation for modern applications.",
    points: ["Scalable API design", "Backend architecture", "System integration", "Data connectivity"],
  },
  {
    title: "Business Automation",
    slug: "business-automation",
    short: "Make repetitive work disappear.",
    long: "We identify time-consuming manual processes and transform them into automated workflows — including reporting, scheduling, notifications, billing, compliance processes, and background operations.",
    points: ["Reporting", "Scheduling", "Notifications", "Billing", "Compliance processes", "Background operations"],
  },
  {
    title: "AI & Intelligent Solutions",
    slug: "ai-intelligent-solutions",
    short: "Make AI useful to your business.",
    long: "We integrate AI into applications and workflows to automate content generation, document analysis, intelligent processing, and other business processes where AI can create meaningful value.",
    points: ["Content generation", "Document analysis", "Intelligent processing", "Workflow integration"],
  },
  {
    title: "Legacy Modernization",
    slug: "legacy-modernization",
    short: "Your existing software shouldn't become a limitation.",
    long: "We modernize legacy applications, improve architecture, optimize databases, migrate older technologies, and improve system performance while preserving valuable business functionality.",
    points: ["Architecture improvement", "Database optimization", "Technology migration", "Performance tuning"],
  },
  {
    title: "Cloud & DevOps",
    slug: "cloud-devops",
    short: "Build for today. Prepare for tomorrow.",
    long: "We help businesses deploy, modernize, and manage applications using cloud technologies, CI/CD practices, containerization, monitoring, and modern DevOps workflows.",
    points: ["Cloud deployment", "CI/CD pipelines", "Containerization", "Monitoring"],
  },
  {
    title: "Application Support & Enhancement",
    slug: "application-support-enhancement",
    short: "Software evolves with your business.",
    long: "We provide ongoing improvements, optimization, maintenance, feature development, and technical support to keep applications reliable and ready for change.",
    points: ["Ongoing improvements", "Optimization", "Maintenance", "Feature development"],
  },
];

export const whyUs = [
  { title: "Business First", desc: "We understand the business problem before designing the technical solution." },
  { title: "Practical Engineering", desc: "We use proven technologies and development practices to build solutions that are maintainable, scalable, and reliable." },
  { title: "Automation Mindset", desc: "We look for opportunities to eliminate repetitive work and improve operational efficiency." },
  { title: "Performance Matters", desc: "From application architecture to database queries, we pay attention to the details that affect real-world performance." },
  { title: "Built to Evolve", desc: "We design systems with future growth, changing requirements, and long-term maintenance in mind." },
  { title: "Flexible Partnership", desc: "Work with us for a complete project, additional development capacity, ongoing engineering support, or a dedicated development engagement." },
];

export const metrics = [
  { value: 60, prefix: "~", suffix: "%", label: "Less manual admin work", desc: "Reduction in manual administrative effort through automated reporting and scheduling." },
  { value: 40, prefix: "~", suffix: "%", label: "Faster invoice processing", desc: "Reduction in invoice processing time through billing automation." },
  { value: 35, prefix: "30–", suffix: "%", label: "Faster reporting", desc: "Improvement in reporting performance through database and query optimization." },
  { value: 40, prefix: "~", suffix: "%", label: "Less downtime", desc: "Reduction in application downtime through legacy application modernization." },
  { value: 30, prefix: "25–", suffix: "%", label: "Faster data retrieval", desc: "Improvement in data retrieval performance through SQL optimization and indexing." },
];

export const solutions = [
  {
    title: "Learning & Compliance",
    slug: "learning-compliance",
    desc: "Enterprise learning and compliance platform supporting training management, certification tracking, reporting, and AI-powered course generation.",
    highlights: ["Training management", "Certification tracking", "Reporting & analytics", "AI-assisted content generation"],
    tech: ["Learning Platforms", "Compliance", "Reporting", "AI"],
  },
  {
    title: "Manufacturing & Warehouse",
    slug: "manufacturing-warehouse",
    desc: "Centralized factory and warehouse management platform supporting inventory, order processing, operational workflows, and reporting.",
    highlights: ["Inventory visibility", "Process workflow management", "Operational reporting", "Order lifecycle control"],
    tech: ["ERP", "Warehouse Ops", "Inventory", "Dashboards"],
  },
  {
    title: "Print & Operations",
    slug: "print-operations",
    desc: "Workflow-driven print-on-demand platform with order management, automated billing, and reporting.",
    highlights: ["Order processing", "Automated billing", "Operational transparency", "Workflow orchestration"],
    tech: ["Print Workflow", "Billing", "Automation", "Operations"],
  },
  {
    title: "Healthcare",
    slug: "healthcare",
    desc: "Application supporting doctor-patient interaction, video consultations, digital prescriptions, and healthcare content delivery.",
    highlights: ["Patient communication", "Video consultations", "Digital prescriptions", "Accessible content delivery"],
    tech: ["Healthcare", "Telemedicine", "UX", "Security"],
  },
  {
    title: "Identity & Kiosk Systems",
    slug: "identity-kiosk-systems",
    desc: "Desktop-based biometric capture and identity verification system designed for structured data collection in kiosk environments.",
    highlights: ["Biometric workflows", "Identity verification", "Structured capture", "Kiosk-friendly UX"],
    tech: ["Biometrics", "Identity", "Kiosk", "Data Capture"],
  },
  {
    title: "Decision Support",
    slug: "decision-support",
    desc: "Decision-analysis application using structured analytical models to support complex decision-making.",
    highlights: ["Scenario modeling", "Analytical decision workflows", "Structured insight", "Business logic support"],
    tech: ["Decision Models", "Analytics", "Business Logic", "Reporting"],
  },
  {
    title: "Real-Time Interaction",
    slug: "real-time-interaction",
    desc: "Mobile application supporting real-time audio/video communication and location-based interaction.",
    highlights: ["Real-time communication", "Location-aware interaction", "Audio & video flows", "User engagement"],
    tech: ["Realtime", "Mobile", "Audio/Video", "Location"],
  },
];

export const process = [
  { step: "01", title: "Discover", desc: "We start by understanding your business, users, requirements, challenges, and goals." },
  { step: "02", title: "Define", desc: "We convert requirements into a clear technical direction, architecture, scope, and development plan." },
  { step: "03", title: "Build", desc: "Our team develops the solution with a focus on quality, performance, security, and maintainability." },
  { step: "04", title: "Validate", desc: "We test, refine, optimize, and prepare the solution for real-world use." },
  { step: "05", title: "Launch", desc: "We deploy the application using appropriate infrastructure and deployment practices." },
  { step: "06", title: "Evolve", desc: "We continue to improve, maintain, and scale the solution as your business grows." },
];

export const techCategories = [
  { name: "Backend", items: ["C#", ".NET", "ASP.NET Core", "REST APIs", "Entity Framework Core", "LINQ"] },
  { name: "Frontend", items: ["Angular", "TypeScript", "JavaScript", "HTML5", "CSS3"] },
  { name: "Data", items: ["SQL Server", "Database Design", "Stored Procedures", "Query Optimization", "Indexing"] },
  { name: "Cloud & DevOps", items: ["Microsoft Azure", "Azure App Services", "Azure Functions", "Azure SQL", "Azure Storage", "Azure Key Vault", "Azure DevOps", "CI/CD", "Docker"] },
  { name: "AI", items: ["Gemini API", "Prompt Engineering", "AI Integration", "AI-Powered Content Generation"] },
  { name: "Engineering", items: ["Clean Architecture", "SOLID Principles", "MVC", "N-Tier Architecture", "Background Services", "Scheduled Jobs"] },
];

export const industries = [
  { title: "Education & Learning", desc: "Learning platforms, training management, certification, compliance, and reporting." },
  { title: "Manufacturing", desc: "Factory operations, workflow management, production processes, and operational systems." },
  { title: "Logistics & Warehousing", desc: "Inventory, order processing, warehouse operations, and reporting." },
  { title: "Healthcare", desc: "Digital healthcare applications, communication platforms, and workflow systems." },
  { title: "Business Operations", desc: "Automation, billing, reporting, management platforms, and internal business systems." },
  { title: "Startups & Growing Businesses", desc: "MVP development, custom platforms, modernization, and scalable technology foundations." },
];

export const engagements = [
  {
    title: "Project-Based Development",
    slug: "project-based-development",
    ask: "Have a defined requirement?",
    desc: "We can take your project from planning and architecture through development, deployment, and delivery.",
    detail: "For teams with a clear scope, timeline, or milestone plan, we bring delivery ownership from discovery through implementation. This model is ideal when you need technical execution, quality code, and a partner who can keep the project moving without adding operational overhead.",
    deliverables: ["Discovery and requirements alignment", "Architecture and technical planning", "Build, testing, and deployment", "Clear milestone-based delivery"],
    idealFor: "Teams that need a trusted technical partner for a defined build or modernization initiative.",
  },
  {
    title: "Dedicated Development",
    slug: "dedicated-development",
    ask: "Need additional development capacity?",
    desc: "Work with dedicated engineering resources aligned with your product, processes, and technology stack.",
    detail: "When your team needs a focused engineering extension, we embed with your process and become a dependable part of delivery. This model gives you access to senior technical capability without the delays, overhead, and hiring cycle of expanding your internal team.",
    deliverables: ["Dedicated engineering support", "Product-aligned delivery cadence", "Continuous iteration and feature progress", "Transparent communication and planning"],
    idealFor: "Businesses that need a reliable software team to support product delivery over time.",
  },
  {
    title: "Extended Technology Team",
    slug: "extended-technology-team",
    ask: "Need a broader technology partner?",
    desc: "We can work alongside your existing organization across development, modernization, maintenance, automation, and technical delivery.",
    detail: "This model is designed for organizations that already have a technical foundation but need deeper capability across architecture, delivery, and modernization. We plug into the work where it matters most — from application support to process automation, technical improvement, and product evolution.",
    deliverables: ["Cross-functional technical partnership", "Modernization and maintenance support", "Automation and operational efficiency", "Architecture and engineering guidance"],
    idealFor: "Companies that want a flexible technology partner supporting ongoing product and platform evolution.",
  },
  {
    title: "Flexible Engagement",
    slug: "flexible-engagement",
    ask: "Need something in between?",
    desc: "Our engagement model can adapt to the needs of the project — from fixed-scope work to ongoing development partnerships.",
    detail: "Not every project fits neatly into a single model. Some teams need a combination of strategy, implementation, support, and iterative delivery. A flexible engagement lets us adapt to your situation and shape the working model around your goals, constraints, and pace.",
    deliverables: ["Custom engagement structure", "Adaptive planning and delivery", "Responsive technical support", "Scope and capacity flexibility"],
    idealFor: "Teams that need a pragmatic partnership model that changes as the work evolves.",
  },
];

export const careerValues = [
  "Curiosity",
  "Ownership",
  "Clean thinking",
  "Continuous learning",
  "Collaboration",
  "Quality engineering",
];

export const futureRoles = [
  {
    title: "Software Development",
    slug: "software-development",
    description: "We are looking for engineers who enjoy turning business problems into reliable software products and practical technical solutions.",
    focusAreas: ["Product development", "Clean architecture", "Problem solving", "Team collaboration"],
    idealFor: "Developers who like building real-world systems with clarity, structure, and ownership.",
  },
  {
    title: "Full-Stack Engineering",
    slug: "full-stack-engineering",
    description: "We work across the stack, from user experience and interfaces to APIs, integrations, and application architecture.",
    focusAreas: ["Frontend delivery", "Backend systems", "API design", "Product thinking"],
    idealFor: "Engineers who are comfortable moving between client and server layers.",
  },
  {
    title: "Frontend Development",
    slug: "frontend-development",
    description: "We value people who create interfaces that are not only attractive but fast, accessible, and intuitive for real users.",
    focusAreas: ["Modern user interfaces", "Interaction design", "Responsive experiences", "Code quality"],
    idealFor: "Design-minded developers who care about detail, usability, and performance.",
  },
  {
    title: "Backend Development",
    slug: "backend-development",
    description: "Strong backend engineering supports performance, security, and maintainability across every product we build.",
    focusAreas: ["API design", "Business logic", "Database architecture", "System reliability"],
    idealFor: "Engineers who enjoy building the dependable foundations behind digital products.",
  },
  {
    title: "QA & Testing",
    slug: "qa-testing",
    description: "We believe quality is built into the process, not added at the end. Good testing helps teams ship with confidence.",
    focusAreas: ["Test strategy", "Regression coverage", "Bug analysis", "Quality assurance"],
    idealFor: "People who care about detail, consistency, and making products dependable.",
  },
  {
    title: "UI/UX Design",
    slug: "uiux-design",
    description: "We want thoughtful designers who understand both product experience and business outcomes.",
    focusAreas: ["User-centered design", "Product flows", "Interface systems", "UX validation"],
    idealFor: "Designers who can turn complexity into clear, useful experiences.",
  },
  {
    title: "Cloud & DevOps",
    slug: "cloud-devops",
    description: "Reliable infrastructure, performance, and deployment practices are essential for modern software products.",
    focusAreas: ["Cloud platforms", "CI/CD", "Automation", "Monitoring and deployment"],
    idealFor: "People who care about resilient systems and efficient delivery workflows.",
  },
  {
    title: "AI & Automation",
    slug: "ai-automation",
    description: "We explore practical AI and automation opportunities where they genuinely improve business workflows and product value.",
    focusAreas: ["Automation workflows", "AI integration", "Business process improvement", "Practical innovation"],
    idealFor: "Builders who enjoy combining technology with operational impact.",
  },
];

export const navLinks = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/solutions", label: "Solutions" },
  { to: "/outsourcing", label: "Outsourcing" },
  { to: "/about", label: "About" },
  { to: "/careers", label: "Careers" },
];
